import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import heroBg from "../../assets/hero-bg2.jpeg"; // Keep your path

// --- DATA ---
const timelineData = {
    categories: [
        { name: 'AI Applications', color: 'bg-aiApplications', border: 'border-aiApplications', text: 'text-aiApplications' },
        { name: 'Healthcare', color: 'bg-healthcare', border: 'border-healthcare', text: 'text-healthcare' },
        { name: 'Dev Tools', color: 'bg-devTools', border: 'border-devTools', text: 'text-devTools' },
        { name: 'Infrastructure', color: 'bg-infrastructure', border: 'border-infrastructure', text: 'text-infrastructure' },
    ],
    investments: [
        { cat: 3, time: '2023-02' }, { cat: 0, time: '2023-03' }, { cat: 3, time: '2023-03' },
        { cat: 2, time: '2023-04' }, { cat: 1, time: '2023-05' }, { cat: 0, time: '2023-06' },
        { cat: 0, time: '2023-07' }, { cat: 3, time: '2023-07' }, { cat: 0, time: '2023-08' },
        { cat: 2, time: '2023-08' }, { cat: 0, time: '2023-09' }, { cat: 1, time: '2023-09' },
        { cat: 2, time: '2023-09' }, { cat: 3, time: '2024-01' }, { cat: 0, time: '2024-02' },
        { cat: 2, time: '2024-04' }, { cat: 3, time: '2024-04' }, { cat: 0, time: '2024-05' },
        { cat: 2, time: '2024-05' }, { cat: 0, time: '2024-07' }, { cat: 1, time: '2024-07' },
        { cat: 3, time: '2024-07' }, { cat: 0, time: '2024-09' }, { cat: 3, time: '2024-09' },
        { cat: 1, time: '2024-10' }, { cat: 0, time: '2025-01' }, { cat: 2, time: '2025-02' },
        { cat: 1, time: '2025-03' }, { cat: 0, time: '2025-06' }, { cat: 3, time: '2025-06' },
        { cat: 0, time: '2025-07' }, { cat: 1, time: '2025-07' }, { cat: 0, time: '2025-08' },
        { cat: 1, time: '2025-08' }, { cat: 3, time: '2025-08' }, { cat: 0, time: '2025-09' },
        { cat: 2, time: '2025-09' }, { cat: 0, time: '2025-10' },
    ]
};

// Helper: Get Month Name
const getMonthName = (dateStr) => new Date(dateStr + '-01').toLocaleString('default', { month: 'long' });

const PortfolioTimeline = () => {
    // Group Data by Year
    const groupedData = useMemo(() => {
        const groups = {};
        timelineData.investments.forEach(item => {
            const year = item.time.split('-')[0];
            if (!groups[year]) groups[year] = [];
            groups[year].push(item);
        });
        // Sort descending or ascending (Ascending here: 2023 -> 2026)
        return Object.keys(groups).sort().map(year => ({
            year,
            items: groups[year]
        }));
    }, []);

    // State for the "Active Year" (Accordion logic)
    const [activeYear, setActiveYear] = useState(null);

    const toggleYear = (year) => {
        setActiveYear(activeYear === year ? null : year);
    };

    return (
        <section className="relative pt-32 pb-32 overflow-hidden min-h-screen">

            {/* --- Original Background (Fixed) --- */}
            <div className="absolute inset-0 z-0 fixed pointer-events-none">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `url(${heroBg})`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center center',
                        backgroundAttachment: 'fixed', // Fixed as requested
                        filter: 'brightness(1) blur(0px)',
                    }}
                />
                <div className="absolute inset-0 bg-gradient-to-r from-neutral-900/30 via-neutral-900/50 to-neutral-900/70"></div>
            </div>

            <div className="container mx-auto px-6 relative z-10">
                <div className="flex flex-col lg:flex-row gap-16">

                    {/* --- LEFT COLUMN: Sticky Title --- */}
                    <div className="lg:w-5/12 xl:w-4/12">
                        <div className="sticky top-32">
                            <motion.div
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                            >
                                <div className="flex items-center gap-2 mb-6">
                                    <span className="w-2 h-2 rounded-full bg-brand animate-pulse"></span>
                                    <span className="h-[1px] w-12 bg-white/20"></span>
                                    <h6 className="text-brand font-bold uppercase tracking-widest text-sm">Timeline</h6>
                                </div>
                                <h2 className="text-4xl md:text-5xl font-bold text-white font-serif leading-tight mb-6">
                                    Investment <br /> Archive
                                </h2>
                                <p className="text-white/60 text-lg mb-8 max-w-sm">
                                    A structured history of our capital deployment. Expand the years to view specific monthly activities.
                                </p>

                                <div className="flex flex-wrap gap-2 mb-10">
                                    {timelineData.categories.map((cat, i) => (
                                        <div key={i} className="flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/5 bg-white/5">
                                            <span className={`w-2 h-2 rounded-full ${cat.color}`}></span>
                                            <span className="text-[10px] uppercase font-bold text-white/70">{cat.name}</span>
                                        </div>
                                    ))}
                                </div>
                            </motion.div>
                        </div>
                    </div>

                    {/* --- RIGHT COLUMN: Scrollable Accordion List --- */}
                    <div className="lg:w-7/12 xl:w-8/12">
                        <div className="flex flex-col gap-4">
                            {groupedData.map((group) => (
                                <YearAccordion
                                    key={group.year}
                                    year={group.year}
                                    items={group.items}
                                    isOpen={activeYear === group.year}
                                    onToggle={() => toggleYear(group.year)}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

// --- COMPONENT: Year Accordion ---
const YearAccordion = ({ year, items, isOpen, onToggle }) => {
    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className={`border rounded-2xl transition-all duration-300 overflow-hidden ${isOpen ? 'bg-white/5 border-brand/50 shadow-lg shadow-brand/10' : 'bg-transparent border-white/10 hover:bg-white/5'}`}
        >
            {/* Year Header / Button */}
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between p-6 md:p-8 cursor-pointer focus:outline-none"
            >
                <div className="flex items-center gap-6">
                    <span className={`text-4xl md:text-5xl font-bold font-serif transition-colors ${isOpen ? 'text-white' : 'text-white/40'}`}>
                        {year}
                    </span>
                    <div className="flex flex-col items-start">
                        <span className="text-sm text-white/50 uppercase tracking-widest">{items.length} Investments</span>
                        {isOpen && <span className="text-xs text-brand mt-1">Active View</span>}
                    </div>
                </div>

                {/* Expand/Collapse Button Icon */}
                <div className={`w-10 h-10 rounded-full border flex items-center justify-center transition-all duration-300 ${isOpen ? 'bg-brand border-brand text-white rotate-180' : 'border-white/20 text-white/40 group-hover:border-white'}`}>
                    <i className="fa-solid fa-chevron-down"></i>
                </div>
            </button>

            {/* Expandable Content Area */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                    >
                        <div className="px-6 pb-8 md:px-8 space-y-3 border-t border-white/5 pt-6">
                            {items.map((item, index) => (
                                <MonthItem key={index} item={item} />
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

// --- COMPONENT: Month/Item Row (Expandable details) ---
const MonthItem = ({ item }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const cat = timelineData.categories[item.cat];
    const monthName = getMonthName(item.time);

    return (
        <div className="group">
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                className={`
                    flex items-center justify-between p-4 rounded-xl cursor-pointer transition-all duration-300
                    ${isExpanded ? 'bg-white/10' : 'bg-white/5 hover:bg-white/10'}
                `}
            >
                {/* Left: Month & Dot */}
                <div className="flex items-center gap-4">
                    <div className={`w-2 h-2 rounded-full ${cat.color} ${isExpanded ? 'animate-pulse' : ''}`}></div>
                    <span className="w-24 font-mono text-sm text-white/70">{monthName}</span>
                    <span className={`text-xs font-bold uppercase tracking-wider ${cat.text} hidden md:block`}>
                        {cat.name}
                    </span>
                </div>

                {/* Right: + Button */}
                <div className={`text-white/30 transition-transform duration-300 ${isExpanded ? 'rotate-45 text-white' : ''}`}>
                    <i className="fa-solid fa-plus"></i>
                </div>
            </div>

            {/* Expanded Details */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                    >
                        <div className="p-4 ml-4 md:ml-10 border-l border-white/10 text-sm text-white/60">
                            <p className="mb-2">
                                <span className={`text-xs font-bold uppercase ${cat.text} md:hidden block mb-1`}>{cat.name}</span>
                                Strategic capital deployed into <span className="text-white">Series A</span> infrastructure.
                            </p>
                            <p>Focus: Scalability, Security, and Automation.</p>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default PortfolioTimeline;